import BaseCache from './BaseCache'

/**
 * 첨부파일 download 결과(DownloadObj)를 감싸는 레코드.
 * blobId는 content-addressed이므로 동일 (blobId, name, type) 조합이면
 * 동일한 리소스를 가리킨다고 가정한다. 짧은 TTL(10분) + outerHTML sweep으로
 * 메모리 누적을 막으며, 메모리 전용 캐시이므로 reload 시 자동 초기화된다.
 *
 * data는 DownloadObj 형태이지만 타입이 apps 레이어에 정의되어 있어
 * libs에서는 의도적으로 opaque(unknown)으로 둔다. 소비자 측에서 캐스팅해 사용한다.
 */
export interface JmapDownloadCacheRecord {
  id: string
  data: unknown
  expireTime?: number | null
  isExpired: () => boolean
}

export interface JmapDownloadCacheFileInfo {
  blobId?: string | null
  name?: string | null
  type?: string | null
}

/**
 * (blobId, name, type)을 ':'로 이어 고유 키를 생성한다.
 * 누락 값은 빈 문자열로 치환한다.
 */
export const buildJmapDownloadCacheKey = (
  file: JmapDownloadCacheFileInfo | null | undefined,
): string => {
  const blobId = file?.blobId ?? ''
  const name = file?.name ?? ''
  const type = file?.type ?? ''
  return `${blobId}:${name}:${type}`
}

/* ────────────────────────────────────────────────────────────────────────────
 * blob URL GC — DOM reachability 기반
 *
 * `URL.createObjectURL`이 만든 모든 blob URL을 `objectURLMap`에 등록(생성 시점)하고,
 * thread/mailbox 전환 시점에 `sweepObjectURLMap()`이 main document + iframe outerHTML에
 * substring 매칭으로 사용 여부를 검사. 어디에도 안 보이면 revoke + delete.
 *
 * cache evict 흐름과는 분리 — 캐시 보유/미보유 race가 sweep 정확성에 영향 없음.
 * ──────────────────────────────────────────────────────────────────────────── */

// ─── 모듈 상수 (텔레메트리 후 조정) ──────────────────────────────────────────

/** 캐시 항목 TTL — 10분. 사용 중인 path는 `getImagePath`의 `touch`로 갱신. */
export const JMAP_DOWNLOAD_CACHE_TTL_MS = 10 * 60 * 1000

/** `BaseCache.shouldDeferEvict` 시 expireTime 연장 폭. */
export const EVICT_DEFER_MS = 60 * 1000

/** 동일 키에서 연속 defer 가능한 최대 횟수. */
export const MAX_DEFER_COUNT = 5

/** sweep을 defer할 `objectURLMap` 크기 임계. 초과 시 다음 idle로 미룸. */
export const SWEEP_MAX_URLS = 500

/** sweep 1회 누적 처리 budget. 청크 사이에 누적 시간이 이 값을 넘으면 yield. */
export const SWEEP_MAX_BUDGET_MS = 50

/** 직전 sweep 종료 후 cooldown — 그 이내 호출은 defer. */
export const SWEEP_COOLDOWN_MS = 500

/** 연속 defer가 이 횟수를 넘으면 강제 1회 실행으로 누수 방지. */
export const MAX_CONSECUTIVE_DEFERS = 5

/**
 * 주기적 백업 sweep 간격.
 * thread/mailbox 전환만으로는 단일 thread 장기 시청 시 sweep이 호출되지 않아
 * 만료된 cache 항목과 그 path가 메모리에 누적된다. TTL의 절반 정도로 도는
 * timer를 둬서 lazy expiration 한계를 보완한다.
 *
 * 비용은 `shouldDeferSweep` 가드(visibility/cooldown/size)와 `objectURLMap.size === 0`
 * 빠른 종료로 비활성 시점엔 거의 0.
 */
export const PERIODIC_SWEEP_INTERVAL_MS = 5 * 60 * 1000

/** sweep 1청크에서 처리할 url 수. */
const SWEEP_CHUNK_SIZE = 20

// ─── 검증용 로그 (console.info + ring buffer) ───────────────────────────────
//
// 사용법(브라우저 콘솔):
//   __gcDump()                                   // 전체 [gc] 로그 텍스트
//   copy(__gcDump())                             // 클립보드로 복사 → /gc-analyze 명령에 붙여넣기
//   __gcLog.length                               // 현재 누적 줄 수
//
// 분석 기준은 .claude/commands/gc-analyze.md 참조.

const MAX_GC_LOG = 2000
const gcLogBuffer: string[] = []

const gcLog = (msg: string): void => {
  console.info(msg)
  const stamp = new Date().toISOString()
  gcLogBuffer.push(`${stamp} ${msg}`)
  if (gcLogBuffer.length > MAX_GC_LOG) gcLogBuffer.shift()
}

if (typeof window !== 'undefined') {
  type GcWin = Window & { __gcLog?: string[]; __gcDump?: () => string }
  ;(window as GcWin).__gcLog = gcLogBuffer
  ;(window as GcWin).__gcDump = () => gcLogBuffer.join('\n')
}

// ─── 추적 set + 헬퍼 ────────────────────────────────────────────────────────

/**
 * `createObjectURL` 호출 직후 등록되는 blob URL의 추적 set.
 * sweep 대상 = 이 set의 항목 중 어떤 outerHTML에도 안 나타나는 것.
 *
 * 외부 관찰/테스트용으로 export하지만, 직접 mutate하지 않는 것이 권장.
 * url 등록은 `trackObjectURL`로, 제거는 sweep이 담당.
 */
export const objectURLMap: Set<string> = new Set()

/**
 * `URL.createObjectURL(...)` 결과를 `objectURLMap`에 등록한 뒤 그대로 반환.
 * 호출자는 `const url = trackObjectURL(window.URL.createObjectURL(blob))` 형태로 사용.
 *
 * 빈 문자열/null/undefined는 무시(반환 null) — 결과적으로 set 크기가
 * 의미 없는 항목으로 부풀지 않도록 한다.
 *
 * 단발 자원(예: viewerExt downloadAll의 zip URL)은 `trackObjectURL` 적용 대상이 아님.
 * 1회성 자원은 호출자가 즉시 `URL.revokeObjectURL`로 정리한다.
 */
export const trackObjectURL = (url: string | null | undefined): string | null => {
  if (typeof url !== 'string' || url.length === 0) return null
  objectURLMap.add(url)
  return url
}

/** revoke 시 발생하는 브라우저 경고를 한곳에서 흡수. */
export const safeRevoke = (url: string): void => {
  try {
    window.URL.revokeObjectURL(url)
  } catch (e) {
    console.warn('[gc] safeRevoke failed:', url, e)
  }
}

// ─── sweep 상태 ─────────────────────────────────────────────────────────────

let sweepInFlight = false
let lastSweepEndAt = 0
let sweepScheduled = false
let consecutiveDefers = 0

/**
 * 큰 set + 짧은 budget 조합에서 끝쪽 url이 영영 처리되지 않는 starvation을 막기 위해
 * 스냅샷과 cursor를 모듈 레벨에 보존. budget 초과로 yield 시 같은 snapshot의
 * `index` 위치부터 다음 청크에서 이어 처리.
 */
let sweepCursor: { snapshot: string[]; index: number } | null = null

/**
 * sweep이 진행 중이거나 cooldown 구간이면 true.
 * BaseCache.shouldDeferEvict에서 사용해 사용 중인 캐시 항목의 evict를 미룬다.
 */
export const isSweepBusy = (): boolean => {
  if (sweepInFlight === true) return true
  if (Date.now() - lastSweepEndAt < SWEEP_COOLDOWN_MS) return true
  return false
}

const shouldDeferSweep = (force: boolean): string | null => {
  if (force === true) return null
  if (sweepInFlight === true) return 'inFlight'
  if (objectURLMap.size > SWEEP_MAX_URLS) return 'size'
  if (Date.now() - lastSweepEndAt < SWEEP_COOLDOWN_MS) return 'cooldown'
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return 'visibility'
  return null
}

const scheduleNextSweep = (): void => {
  if (sweepScheduled === true) return
  sweepScheduled = true

  const run = (): void => {
    sweepScheduled = false
    sweepObjectURLMap()
  }

  type IdleWindow = Window & { requestIdleCallback?: (cb: () => void) => unknown }
  const w = (typeof window !== 'undefined' ? window : undefined) as IdleWindow | undefined
  if (w?.requestIdleCallback != null) {
    w.requestIdleCallback(run)
  } else {
    setTimeout(run, 0)
  }
}

/**
 * sweep의 in-use 검사 신호를 두 채널로 모은다.
 *
 * - `htmlList`: main document body + 일반 iframe contentDocument body의 outerHTML
 *   substring 매칭 — viewer iframe의 `<img src=blob:>`, 첨부 element의 `data-blob-url`,
 *   첨부 프리뷰 모달(showImage/showVideo) 등을 한 번에 커버.
 * - `editorUrls`: EmailForm 편집기 iframe(tinymce)의 `editorWindow.editorMgr.cidMap`이
 *   보유한 blob URL Set. tinymce는 본문 src에 `cid:` 토큰을 두고 실제 blob URL은 cidMap에만
 *   보유하므로 iframe outerHTML 매칭으로는 잡히지 않는다. cidMap.values()를 별도 채널로
 *   본다.
 */
const collectInUseSignals = (): { htmlList: string[]; editorUrls: Set<string> } => {
  const htmlList: string[] = []
  const editorUrls = new Set<string>()
  if (typeof document === 'undefined') return { htmlList, editorUrls }

  if (document.body != null) {
    htmlList.push(document.body.outerHTML)
  }

  const iframes = document.querySelectorAll('iframe')
  iframes.forEach((ifr) => {
    try {
      const frame = ifr as HTMLIFrameElement
      // EmailForm 편집기 iframe은 tinymce가 cidMap에만 blob URL을 보유한다.
      // contentWindow에서 직접 cidMap을 읽어 in-use 신호로 추가.
      type EditorWin = {
        editorMgr?: { cidMap?: Record<string, string> | null } | null
      } | null
      const win = frame.contentWindow as unknown as EditorWin
      const cidMap = win?.editorMgr?.cidMap
      if (cidMap != null) {
        Object.values(cidMap).forEach((url) => {
          if (typeof url === 'string' && url.length > 0) editorUrls.add(url)
        })
      }

      const doc = frame.contentDocument
      if (doc?.body != null) {
        htmlList.push(doc.body.outerHTML)
      }
    } catch {
      // cross-origin iframe — 접근 불가, skip
    }
  })

  return { htmlList, editorUrls }
}

/**
 * `jmapDownloadCache.dataMap`이 현재 보유 중인 DownloadObj의 path/downloadPath를 모은다.
 *
 * sweep이 DOM 사용 여부만 검사하면, 사용자가 thread를 잠깐 떠난 사이 cache는 보유 중인데
 * DOM엔 노출 안 된 path가 revoke되고, 다시 같은 메일로 돌아왔을 때 cache hit으로 반환된
 * `DownloadObj`의 path가 죽어있어 이미지가 깨진다(D16의 "자연 복구"는 cache miss 경로만
 * 보호). 따라서 sweep 시점에 cache 보유 path도 함께 보호 신호로 본다.
 *
 * cache evict 훅에 의존하지 않고 sweep 시점에 한 번 스냅샷을 잡는 방식이라
 * D11("cache evict race와 sweep 정확성 분리") 정신은 유지된다.
 */
const collectCachePaths = (): Set<string> => {
  const set = new Set<string>()
  const dataMap = jmapDownloadCache.dataMap as Record<string, JmapDownloadCacheRecord | undefined>
  for (const key in dataMap) {
    const record = dataMap[key]
    const data = record?.data as { path?: unknown; downloadPath?: unknown } | null | undefined
    if (data == null) continue
    if (typeof data.path === 'string' && data.path.length > 0) set.add(data.path)
    if (typeof data.downloadPath === 'string' && data.downloadPath.length > 0) {
      set.add(data.downloadPath)
    }
  }
  return set
}

const isUrlInUse = (url: string, htmlList: string[]): boolean => {
  for (let i = 0; i < htmlList.length; i += 1) {
    if (htmlList[i].indexOf(url) >= 0) return true
  }
  return false
}

/**
 * `objectURLMap`에 등록된 url 중 어디에도 보이지 않는 것을 revoke + delete.
 *
 * - 사전 가드(`shouldDeferSweep`)로 size/cooldown/visibility/sweepInFlight 검사.
 *   초과 시 `scheduleNextSweep`으로 미루고 즉시 종료. 연속 defer가 임계를 넘기면 강제 실행.
 * - 청크 처리(`SWEEP_CHUNK_SIZE`)와 budget 측정(`SWEEP_MAX_BUDGET_MS`)으로
 *   메인 스레드 점유를 제한. budget 초과 시 yield + 같은 cursor에서 재개.
 * - 매 청크에서 outerHTML을 다시 수집해 새로 마운트된 element도 반영.
 */
export const sweepObjectURLMap = (): void => {
  const deferReason = shouldDeferSweep(false)
  if (deferReason != null) {
    consecutiveDefers += 1
    if (consecutiveDefers <= MAX_CONSECUTIVE_DEFERS) {
      scheduleNextSweep()
      return
    }
    // 임계 초과 — 강제 실행
  }

  if (objectURLMap.size === 0 && sweepCursor == null) {
    consecutiveDefers = 0
    return
  }

  // BaseCache는 lazy expiration이라 `getData`/`getAllData` 호출이 없으면 만료된 항목이
  // dataMap에 영구히 남는다. sweep 진입 시 한 번 호출해 만료 항목을 정리하면
  // `collectCachePaths()`가 살아있는 path만 반환하고, sweep이 미사용 path를 정상 회수.
  // hook이 noop이라 이 호출 자체의 비용은 무시 가능. 비동기 무시.
  jmapDownloadCache.getAllData().catch(() => {
    // hook은 noop이지만 만에 하나의 실패는 sweep 진행을 막지 않도록 흡수.
  })

  const isResume = sweepCursor != null
  sweepInFlight = true
  consecutiveDefers = 0
  const startedAt =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now()

  if (sweepCursor == null) {
    sweepCursor = { snapshot: Array.from(objectURLMap), index: 0 }
    gcLog(`[gc] sweep:start size=${objectURLMap.size} snapshot=${sweepCursor.snapshot.length}`)
  }

  // 한 sweep cycle 동안 누적되는 통계.
  let revokedThisCycle = 0
  let keptByDomThisCycle = 0
  let keptByEditorThisCycle = 0
  let keptByCacheThisCycle = 0

  const finish = (yielded: boolean): void => {
    sweepInFlight = false
    lastSweepEndAt = Date.now()
    const elapsedMs =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? (performance.now() - startedAt).toFixed(1)
        : `${Date.now() - startedAt}`
    const keptTotal = keptByDomThisCycle + keptByEditorThisCycle + keptByCacheThisCycle
    gcLog(
      `[gc] sweep:end revoked=${revokedThisCycle} kept=${keptTotal}` +
        `(dom=${keptByDomThisCycle},editor=${keptByEditorThisCycle},cache=${keptByCacheThisCycle})` +
        ` elapsed=${elapsedMs}ms yielded=${yielded ? '1' : '0'} remaining=${objectURLMap.size}` +
        ` resume=${isResume ? '1' : '0'}`,
    )
  }

  const processChunk = (): void => {
    const cursor = sweepCursor
    if (cursor == null) {
      finish(false)
      return
    }

    // 매 청크에서 다시 수집해 sweep 도중 마운트된 element / 변경된 cache 보유 상태도 반영.
    const { htmlList, editorUrls } = collectInUseSignals()
    const cachePaths = collectCachePaths()

    const chunkEnd = Math.min(cursor.index + SWEEP_CHUNK_SIZE, cursor.snapshot.length)
    for (let i = cursor.index; i < chunkEnd; i += 1) {
      const url = cursor.snapshot[i]
      if (objectURLMap.has(url) !== true) continue // 외부에서 이미 delete된 항목 skip
      // 1) 일반 DOM(viewer iframe + main document outerHTML) 매칭
      if (isUrlInUse(url, htmlList) === true) {
        keptByDomThisCycle += 1
        continue
      }
      // 2) EmailForm 편집기 cidMap — tinymce는 src에 cid:만 두고 blob URL은 cidMap에만 보유
      if (editorUrls.has(url) === true) {
        keptByEditorThisCycle += 1
        continue
      }
      // 3) cache 보유 — DOM 어디에도 없지만 다음 cache hit으로 다시 사용될 수 있음
      if (cachePaths.has(url) === true) {
        keptByCacheThisCycle += 1
        continue
      }
      objectURLMap.delete(url)
      safeRevoke(url)
      revokedThisCycle += 1
    }
    cursor.index = chunkEnd

    if (cursor.index >= cursor.snapshot.length) {
      sweepCursor = null
      finish(false)
      return
    }

    // budget 초과 시 yield + 다음 sweep으로 재개
    const now =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
    if (now - startedAt >= SWEEP_MAX_BUDGET_MS) {
      finish(true)
      scheduleNextSweep()
      return
    }

    type IdleWindow = Window & { requestIdleCallback?: (cb: () => void) => unknown }
    const w = (typeof window !== 'undefined' ? window : undefined) as IdleWindow | undefined
    if (w?.requestIdleCallback != null) {
      w.requestIdleCallback(processChunk)
    } else {
      setTimeout(processChunk, 0)
    }
  }

  processChunk()
}

// ─── 캐시 인스턴스 ──────────────────────────────────────────────────────────

const jmapDownloadCache = new BaseCache<JmapDownloadCacheRecord>('jmapDownloadCache', {
  key: 'id',
  // 짧은 TTL(10분). 사용 중인 path는 getImagePath의 touch로 갱신.
  expireDuration: JMAP_DOWNLOAD_CACHE_TTL_MS,
  // miss placeholder는 영구 유지(putData로 덮어쓰기되거나 null 반환으로 재시도 가능).
  expireDurationForEmpty: null,
  // sweep 부하가 높을 때 evict를 미뤄 메모리 정리만 늦춤(사용자 영향 없음).
  shouldDeferEvict: () => isSweepBusy() || objectURLMap.size > SWEEP_MAX_URLS,
  evictDeferMs: EVICT_DEFER_MS,
  maxDeferCount: MAX_DEFER_COUNT,
  // beforeRemoveSaveData는 도입하지 않음 — 추적은 createObjectURL 시점에 끝남.
  // path/downloadPath의 revoke는 sweep이 단일 책임으로 담당.
})

/**
 * inline 이미지 path를 사용하기 직전에 호출.
 * - cache 보유면 `touch`로 TTL 재시작 → evict 미루기.
 * - cache miss(`touch === false`)인데 ret.path가 살아있는 race에서는
 *   `objectURLMap.add(ret.path)`로 보강 → 다음 sweep cycle에서 정상 검사.
 * - ret/path가 null이면 null 반환.
 */
export function getImagePath(
  ret: { path?: string | null } | null | undefined,
  file: JmapDownloadCacheFileInfo | null | undefined,
): string | null {
  if (ret == null || typeof ret.path !== 'string' || ret.path.length === 0) return null

  const key = buildJmapDownloadCacheKey(file)
  const ok = jmapDownloadCache.touch(key)
  if (ok === false) {
    // race: cache는 이미 evict됐지만 ret 인스턴스의 path는 살아있음.
    // 추적 set에 다시 등록해 sweep 사이클에서 정상 보호/회수.
    objectURLMap.add(ret.path)
  }
  return ret.path
}

// ─── 주기적 백업 sweep ───────────────────────────────────────────────────────
//
// 단일 thread 장기 시청처럼 thread/mailbox 전환이 없는 시나리오에서도
// 만료된 cache 항목과 그 path가 회수되도록 timer로 sweep을 주기적으로 돌린다.
// `shouldDeferSweep`이 visibility/cooldown/size를 막으므로 비활성 사용자에게 부담 없음.

let periodicSweepTimer: ReturnType<typeof setInterval> | null = null

const isTestEnv = (): boolean => {
  // vitest는 NODE_ENV를 'test'로 설정. 단위 테스트에서 timer가 process를 hang시키지 않도록.
  try {
    return typeof process !== 'undefined' && process.env?.NODE_ENV === 'test'
  } catch {
    return false
  }
}

if (typeof window !== 'undefined' && periodicSweepTimer == null && isTestEnv() === false) {
  periodicSweepTimer = setInterval(() => {
    sweepObjectURLMap()
  }, PERIODIC_SWEEP_INTERVAL_MS)
}

/**
 * 주기적 sweep 타이머를 정지한다(테스트 cleanup 또는 hot reload 등).
 * production 코드에서 호출할 일은 없다.
 */
export const stopPeriodicSweep = (): void => {
  if (periodicSweepTimer != null) {
    clearInterval(periodicSweepTimer)
    periodicSweepTimer = null
  }
}

export default jmapDownloadCache
